<template>
  <article>
    <activity-stage
      :customRootContainerClass="`write-my-name ${keyboardEnabled ? '' : 'full-height'}`"
      :customContentContainerStyles="{
        height: 'calc(100% - 96px)'
      }"
    >
      <template slot="instructions">
        <slot name="instructions"></slot>
      </template>
      <article slot="content">
        <write-my-name
          v-if="showWriting"
          :show-done="isDoneBtnEnabled"
          :written-name="writtenName | capitalize"
          :on-done-click="showDisplayMyName"
        >
        </write-my-name>

        <display-my-name
          v-if="showPrint"
          :show-done="isDoneBtnEnabled"
          :show-print="!isIOSApp"
          :end-round="handleRoundEnd"
          :open-print-dialog="openPrintDialog"
          :written-name="writtenName | capitalize"
        >
        </display-my-name>
      </article>
    </activity-stage>
    <slot name="keyboard"></slot>
  </article>
</template>

<script>
import { TweenMax, TimelineLite } from 'gsap';
import { mapState } from 'vuex';

import AssetLoader from 'mixins/AssetLoader';
import ActivityManager from 'mixins/ActivityManager';
import TypingActivityHelper from 'mixins/TypingActivityHelper';
import ErrorLogger from 'mixins/ErrorLogger';

import ActivityStage from 'components/activity/ActivityStage.vue';
import DisplayMyName from 'components/svg-components/write-my-name/displayMyName.vue';
import WriteMyName from 'components/svg-components/write-my-name/writeMyName.vue';

const TAB = 'tab';
const BACKSPACE = 'backspace';
const ENTER = 'enter';

export default {
  mixins: [AssetLoader, ActivityManager, ErrorLogger, TypingActivityHelper],

  components: {
    ActivityStage,
    WriteMyName,
    DisplayMyName
  },

  created() {
    this.setFreeTextTyping(true);
  },

  data() {
    return {
      writtenName: '',
      activityName: 'WriteMyName',
      showPrint: false,
      showWriting: true,
      isPrintBtnEnabled: false
    };
  },
  computed: {
    ...mapState('application', {
      isIOSApp: state => state.device.isIOSApp
    }),
    isDoneBtnEnabled() {
      return this.writtenName.length >= 2;
    }
  },
  methods: {
    async loadActivity() {
      this.createRounds();
    },

    getKeyboardConfig() {
      const { keyboardType, roofs } = this.activityData.keyboard;

      return {
        keyboardType,
        roofs
      };
    },

    startActivity() {
      this.setupNewRound();
      this.enableKeyboard();
      this.errorLoggerStart();

      // fade in on next tick
      this.$nextTick(() => {
        this.fadeInWriteMyName();
        this.setActivityContainerHeight();
      });
    },

    handleRoundEnd() {
      this.setFreeTextTyping(false);
      this.completeActivity();
    },

    openPrintDialog() {
      window.print();
    },

    fadeInWriteMyName() {
      TweenMax.to('#write-my-name-svg', 1, { css: { opacity: 1 } });
    },

    fadeInDisplayMyName() {
      new TimelineLite().to('#display-my-name-svg', 0.5, { css: { opacity: 1 } });
    },

    fadeOutWriteMyName() {
      this.showWriting = false;
      this.showPrint = true;

      // on next tick we fade in display-my-name
      this.$nextTick(this.fadeInDisplayMyName);
    },

    showDisplayMyName() {
      this.hideKeyboard();

      new TimelineLite()
        .to('#write-my-name-svg', 0.5, { css: { opacity: 0 } })
        .call(this.fadeOutWriteMyName);
    },

    handleCorrectKey(attempt) {
      const { character } = attempt;
      this.sndType();

      this.captureKeyboardAttemptSegment(attempt, this.isEndOfCurrentSentence);

      const typingArea = document.getElementById('typing-area');
      typingArea?.blur();
      const chr = character.toLowerCase();
      const isBackspace = chr === BACKSPACE;
      const svgWidth = typingArea?.getBoundingClientRect().width;
      const textLength = document.getElementById('text-area').getBoundingClientRect().width + 50;
      const limitCharNr = svgWidth < textLength && !isBackspace;
      const isZoomingWithKeyboard = attempt.withCmd && ['-', '='].includes(char);
      const skipHandling = chr === ENTER || chr === TAB || limitCharNr || isZoomingWithKeyboard;

      // skip handling for enter/tab
      if (skipHandling) return;

      if (isBackspace) {
        // remove last character if backspace
        this.writtenName = this.writtenName.slice(0, -1);
      } else {
        // append current character to name
        this.writtenName = this.writtenName.concat(character);
      }
    }
  }
};
</script>

<!-- eslint-disable max-len -->
<style lang="scss">
.write-my-name .btn-common {
  border-image: url('data:image/svg+xml;charset=US-ASCII,%3Csvg%20xmlns%3D%22http%3A//www.w3.org/2000/svg%22%20width%3D%22210.8%22%20height%3D%2270.3%22%20viewBox%3D%220%200%20210.8%2070.3%22%3E%0A%20%20%3Cpath%20fill%3D%22%23231F20%22%20d%3D%22M.2%2035C.1%2025.3%200%2015.7%200%206c0-1.6.6-3%201.9-4.2C3.2.6%204.6%200%206.3%200c6.6%200%2013.2%200%2019.8.1%206.6.1%2013.2.1%2019.8.1%2013.2.1%2026.4.1%2039.6.1%206.6%200%2013.2-.1%2019.8-.2%206.7%200%2013.3-.1%2019.8-.1%206.7%200%2013.2%200%2019.9.1%206.6.1%2013.2.1%2019.8.1h39.6c1.6%200%203.1.5%204.4%201.7%201.3%201.1%202%202.5%202%204.2%200%204.8-.2%209.7-.5%2014.5-.3%204.8-.4%209.7-.4%2014.5%200%209.7%200%2019.3.1%2029%200%201.7-.5%203-1.6%204.1-1.1%201.1-2.4%201.7-4%201.7-6.6%200-13.2%200-19.8.1s-13.2.1-19.8.1-13.2%200-19.8.1c-6.6.1-13.2.1-19.8.1-6.6%200-13.2%200-19.8-.1-6.6-.1-13.2-.1-19.8-.1-6.6%200-13.2%200-19.8.1-6.6.1-13.2.1-19.8.1-6.6%200-13.2-.1-19.8-.2-6.6-.2-13.2-.3-19.8-.3-1.7%200-3.1-.5-4.2-1.6S.5%2065.7.5%2064.1c0-4.8-.1-9.7-.1-14.5S.2%2039.9.2%2035M6.4%201.8c-1.1%200-2.1.4-3.1%201.3-.9.9-1.4%201.9-1.4%203%200%204.8.1%209.7.2%2014.5.2%204.8.3%209.7.2%2014.5%200%204.8%200%209.7-.1%2014.5s-.1%209.7-.1%2014.5c0%201.1.4%202.1%201.2%202.9.8.8%201.8%201.2%202.9%201.2%206.6%200%2013.2-.1%2019.8-.2%206.6-.2%2013.2-.3%2019.8-.3%206.6%200%2013.2.2%2019.8.5%206.6.3%2013.2.5%2019.8.5%2013.2-.1%2026.4-.2%2039.6-.2%2013.2-.1%2026.4-.2%2039.6-.2%206.6%200%2013.2%200%2019.8-.1%206.8-.2%2013.3-.2%2020-.2%201.1%200%202-.4%202.7-1.2.7-.8%201-1.7%201-2.8%200-4.8%200-9.7.1-14.5.1-4.8.1-9.6.1-14.5s0-9.7-.1-14.5c-.1-4.8-.2-9.7-.2-14.5%200-1.1-.4-2.1-1-3-.7-.9-1.6-1.3-2.6-1.3l-39.6.3c-5.5%200-12.1%200-19.8.2-5.9.1-12.5.1-19.8.1-13.2%200-26.4%200-39.6.2-6.6%200-13.2-.1-19.8-.2-6.6-.1-13.2-.2-19.8-.2-6.6%200-13.2%200-19.8-.1-6.6-.2-13.2-.2-19.8-.2z%22/%3E%0A%3C/svg%3E%0A');
  border-image-slice: 20 20 fill;
  border-image-width: 15px;
  border-image-outset: 0;
  border-image-repeat: stretch;
  border-style: solid;
  text-transform: uppercase;
  font-size: 24px;
  border-radius: 5px;
  background-color: red;
  padding: 10px 40px 5px;
  background-color: white;
  font-family: 'hwtfutura-semibold';
  display: none;
  background-color: #cccccc;
}

.write-my-name .write-button {
  &.enabled {
    display: block;
  }
}

.write-my-name .display-button {
  margin-right: 20px;

  &.enabled {
    display: inline-block;
  }
}

@media print {
  .debugPanel,
  .kwt-toolbar,
  .activity-instructions,
  .brain,
  .write-my-name .keyboardid,
  .write-my-name .textfield-container,
  .write-my-name .keyboard,
  .write-my-name .keyboard-wrapper,
  .write-my-name .btn-common,
  .write-my-name .btn-common.enabled {
    display: none !important;
  }
}
</style>
<!-- eslint-disable max-len -->
